import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './auth/AuthProvider';
import ProtectedRoute from './auth/ProtectedRoute';
import AssistantPanel from './components/Assistant/AssistantPanel';
import { ChatProvider } from './contexts/ChatContext';
import Header from './components/Layout/Header';
import Sidebar from './components/Layout/Sidebar';
import useStore from './store/useStore';
import Dashboard from './pages/Dashboard';
import MyAssets from './pages/MyAssets';
import AssetDetail from './pages/AssetDetail';
import Threats from './pages/Threats';
import Findings from './pages/Findings';
import Playbooks from './pages/Playbooks';
import Advisor from './pages/Advisor';
import OrgMembers from './pages/OrgMembers';
import NetworkMap from './pages/NetworkMap';
import BaselineManagement from './pages/BaselineManagement';
import MyCERTInsights from './pages/MyCERTInsights';
import PlatformOrganizations from './pages/PlatformOrganizations';
import Settings from './pages/Settings';
import Vulnerabilities from './pages/Vulnerabilities';
import SecurityAlerts from './pages/SecurityAlerts';
import AgentRegistry from './pages/AgentRegistry';
import ImportAssets from './pages/ImportAssets';
import EnrichmentFlowDemo from './pages/EnrichmentFlowDemo';
import DetectionRules from './pages/DetectionRules';
import Cases from './pages/Cases';
import CaseDetail from './pages/CaseDetail';
import ResponseActions from './pages/ResponseActions';
import Login from './auth/Login';
import InvitationAccept from './pages/InvitationAccept';

const AppLayout = ({ children }) => {
  const { sidebarMinimized } = useStore();
  return (
    <div className="flex flex-col h-screen bg-dark-900">
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        <main className={`flex-1 min-w-0 overflow-y-auto overflow-x-hidden bg-dark-900 ${sidebarMinimized ? 'md:ml-16' : 'md:ml-60'} transition-all duration-300 ease-in-out`}>
          {children}
        </main>
        {/* Docked assistant: pushes the page over instead of covering it (Ctrl J). */}
        <AssistantPanel />
      </div>
    </div>
  );
};

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/invitation/accept" element={<InvitationAccept />} />
      <Route path="/" element={
        <ProtectedRoute>
          <AppLayout>
            <Dashboard />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/assets" element={
        <ProtectedRoute requiredPermissions={['assets:read']}>
          <AppLayout>
            <MyAssets />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/assets/:id" element={
        <ProtectedRoute requiredPermissions={['assets:read']}>
          <AppLayout>
            <AssetDetail />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/import" element={
        <ProtectedRoute requiredPermissions={['assets:create']}>
          <AppLayout>
            <ImportAssets />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/security-alerts" element={
        <ProtectedRoute requiredPermissions={['events:read']}>
          <AppLayout>
            <SecurityAlerts />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/threats" element={<Navigate to="/security-alerts" replace />} />
      <Route path="/findings" element={
        <ProtectedRoute requiredPermissions={['events:read']}>
          <AppLayout>
            <Findings />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/response-actions" element={
        <ProtectedRoute requiredPermissions={['actions:read']}>
          <AppLayout>
            <ResponseActions />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/playbooks" element={
        <ProtectedRoute requiredPermissions={['playbooks:view']}>
          <AppLayout>
            <Playbooks />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/advisor" element={
        <ProtectedRoute requiredPermissions={['playbooks:view']}>
          <AppLayout>
            <Advisor />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/org/members" element={
        <ProtectedRoute requiredPermissions={['users:read']}>
          <AppLayout>
            <OrgMembers />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute requiredPermissions={['settings:read']}>
          <AppLayout>
            <Settings />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/network-map" element={
        <ProtectedRoute requiredPermissions={['assets:read']}>
          <AppLayout>
            <NetworkMap />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/vulnerabilities" element={<Navigate to="/security-alerts" replace />} />
      <Route path="/agents" element={
        <ProtectedRoute requiredPermissions={['assets:read']}>
          <AppLayout>
            <AgentRegistry />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/demo-flow" element={
        <ProtectedRoute>
          <AppLayout>
            <EnrichmentFlowDemo />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/baseline" element={
        <ProtectedRoute requiredPermissions={['assets:read']}>
          <AppLayout>
            <BaselineManagement />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/mycert" element={
        <ProtectedRoute requiredPermissions={['events:read']}>
          <AppLayout>
            <MyCERTInsights />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/indicator-vault" element={<Navigate to="/security-alerts?private=1" replace />} />
      <Route path="/platform/organizations" element={
        <ProtectedRoute requiredRoles={['Superadmin']}>
          <AppLayout>
            <PlatformOrganizations />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/rules" element={
        <ProtectedRoute requiredPermissions={['events:read']}>
          <AppLayout>
            <DetectionRules />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/cases" element={
        <ProtectedRoute requiredPermissions={['events:read']}>
          <AppLayout>
            <Cases />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/cases/:caseId" element={
        <ProtectedRoute requiredPermissions={['events:read']}>
          <AppLayout>
            <CaseDetail />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return { r, g, b };
}

function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}

function hslToHex(h, s, l) {
  s /= 100; l /= 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r, g, b;
  if (h < 60) { r = c; g = x; b = 0; }
  else if (h < 120) { r = x; g = c; b = 0; }
  else if (h < 180) { r = 0; g = c; b = x; }
  else if (h < 240) { r = 0; g = x; b = c; }
  else if (h < 300) { r = x; g = 0; b = c; }
  else { r = c; g = 0; b = x; }
  const toHex = (v) => Math.round((v + m) * 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function deriveBgShades(hex) {
  const { r, g, b } = hexToRgb(hex);
  const { h, s, l } = rgbToHsl(r, g, b);
  return {
    950: hslToHex(h, s, Math.max(l - 20, 2)),
    900: hex,
    800: hslToHex(h, Math.max(s - 5, 0), Math.min(l + 6, 95)),
    700: hslToHex(h, Math.max(s - 8, 0), Math.min(l + 12, 95)),
  };
}

function App() {
  const theme = useStore((state) => state.theme);
  const accentColor = useStore((state) => state.accentColor);
  const customBg = useStore((state) => state.customBg);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    const root = document.documentElement;
    const isDefault = accentColor === '#F97316';
    const { r, g, b } = hexToRgb(accentColor);

    root.style.setProperty('--accent', accentColor);
    root.style.setProperty('--accent-r', r);
    root.style.setProperty('--accent-g', g);
    root.style.setProperty('--accent-b', b);
    root.style.setProperty('--accent-rgb', `${r}, ${g}, ${b}`);
    root.setAttribute('data-accent', isDefault ? 'default' : 'custom');
  }, [accentColor]);

  useEffect(() => {
    const root = document.documentElement;
    // Custom backgrounds are dark shades and the data-custom-bg rules use
    // !important, so leaving them active in light mode pins every bg-dark-*
    // surface to dark while the rest of the light theme flips.
    if (customBg && theme === 'dark') {
      const shades = deriveBgShades(customBg);
      root.style.setProperty('--dark-950', shades[950]);
      root.style.setProperty('--dark-900', shades[900]);
      root.style.setProperty('--dark-800', shades[800]);
      root.style.setProperty('--dark-700', shades[700]);
      root.setAttribute('data-custom-bg', 'active');
    } else {
      root.style.removeProperty('--dark-950');
      root.style.removeProperty('--dark-900');
      root.style.removeProperty('--dark-800');
      root.style.removeProperty('--dark-700');
      root.removeAttribute('data-custom-bg');
    }
  }, [customBg, theme]);

  return (
    <Router>
      <AuthProvider>
        <ChatProvider>
          <AppRoutes />
        </ChatProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
